/**
 * Public procurement wins, on the company page.
 *
 * What this answers that nothing else on the page does: who this company has
 * SOLD to, and for how much. A contract a municipality signed with a web agency
 * is the closest thing to a reference the register can give, and the same data
 * read the other way round shows which competitors win the work we sell.
 *
 * The card is absent when the mirror holds no win, for the reason the licence
 * and arrears cards are: an empty list is both "never won" and "the import has
 * never run", and a card saying "no wins" cannot tell those apart. It also
 * cannot see anything below the procurement threshold, which is where most
 * small web projects live - the hint under the list says so.
 *
 * The amount is the WINNING TENDER's value, and it is not money paid. Both ways
 * it can mislead are said on the card rather than left to be guessed. A
 * framework agreement's value is its ceiling - or, as measured on a hospital's
 * web framework won for "35 €", a unit price (marked on the row). A consortium
 * member carries the whole consortium's tender (in the hint).
 */
import { useState } from "react";
import { ExternalLink, Landmark } from "lucide-react";
import { Button } from "@wa/ui";
import { StatusPill, fmtMoney } from "../../../ui";
import type { ArDetail } from "../../../../lib/ariregister";
import { procurementPageUrl, type ArProcurementWin } from "../../../../lib/procurement";
import { Card, fmtDate } from "../detailCard";
import { winAmount, winContext, winHeadline } from "./lib";

type T = (key: string, vars?: Record<string, string | number>) => string;

/** Rows before "show all". The busiest winners take hundreds of contracts. */
const PREVIEW = 8;

function WinRow({ w, t }: { w: ArProcurementWin; t: T }) {
  const context = winContext(w);
  const amount = winAmount(w);
  return (
    <li className="border-b border-border/60 py-2 last:border-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="crm-tnum shrink-0 text-[11px] text-muted-foreground">{fmtDate(w.publishedAt)}</span>
        <span className="min-w-0 text-sm font-medium">{winHeadline(w)}</span>
        {w.framework ? <StatusPill tone="wt">{t("ariregister.procurement.framework")}</StatusPill> : null}
        <span className="crm-tnum ml-auto text-sm">
          {amount ?? (
            <span className="text-[11px] text-muted-foreground">{t("ariregister.procurement.noAmount")}</span>
          )}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-[11px] text-muted-foreground">
        {context ? <span className="min-w-0">{context}</span> : null}
        <span>{t("ariregister.procurement.buyer", { buyer: w.buyerName ?? "-" })}</span>
        {w.referenceNumber ? (
          <a
            href={procurementPageUrl(w.referenceNumber)}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 underline"
          >
            {t("ariregister.procurement.open", { ref: w.referenceNumber })}
            <ExternalLink className="size-3" />
          </a>
        ) : null}
      </div>
    </li>
  );
}

export function ProcurementCard({ e, t }: { e: ArDetail; t: T }) {
  const [showAll, setShowAll] = useState(false);
  const wins = e.procurementWins ?? [];
  if (wins.length === 0) return null;

  const shown = showAll ? wins : wins.slice(0, PREVIEW);
  // The head reads the denormalized figures rather than counting the rows: the
  // rows are capped for the payload, the figures are not.
  const count = e.procurementWinCount ?? 0;

  return (
    <Card
      title={t("ariregister.procurement.title")}
      icon={Landmark}
      aside={
        <span className="crm-tnum text-[11px] text-muted-foreground">
          {t("ariregister.procurement.count", { n: count })}
          {e.procurementWinCents != null
            ? ` · ${t("ariregister.procurement.total", { sum: fmtMoney(e.procurementWinCents) })}`
            : ""}
        </span>
      }
    >
      <ul className="mb-2">
        {shown.map((w) => (
          <WinRow key={w.id} w={w} t={t} />
        ))}
      </ul>
      {wins.length > PREVIEW ? (
        <Button variant="link" size="sm" className="h-auto px-0 text-xs" onClick={() => setShowAll((v) => !v)}>
          {showAll
            ? t("ariregister.procurement.showLess")
            : t("ariregister.procurement.showAll", { n: wins.length - PREVIEW })}
        </Button>
      ) : null}
      <p className="mt-2 text-[11px] text-muted-foreground">{t("ariregister.procurement.hint")}</p>
    </Card>
  );
}
